import LCASForm from '@scripts/components/Form';
import LCASSwitcher from '@scripts/components/Switcher';

/**
 * Modal component
 */
export default class {
  /**
   * Ajax configuration
   *
   * @private
   *
   * @var {object}
   */
  #config;

  /**
   * @private
   *
   * @var {?HTMLElement}
   */
  #element;

  /**
   * @private
   *
   * @var {Object<string, LCASForm>}
   */
  #forms;

  /**
   * @private
   *
   * @var {string}
   */
  #currentForm = '';

  /**
   * @private
   *
   * @var {LCASSwitcher}
   */
  #switcher;

  /**
   * @param {object} config Modal configuration for AJAX request
   */
  constructor(config) {
    this.#config = config;
    if (!this.#config) {
      throw new Error(
        'Licorn Alert Subscriber - Modal: configuration is required.'
      );
    }

    this.#element = document.querySelector('#licornAlertSubModal');
    if (!this.#element) {
      throw new Error('Licorn Alert Subscriber - Modal: element is required.');
    }

    this.#switcher = new LCASSwitcher(
      this.#element.querySelector('.lcas-switcher')
    );

    this.#init();
  }

  /**
   * Initialize the modal
   *
   * @return {void}
   */
  #init() {
    this.#initListeners();
    this.#initForms();
  }

  /**
   * Send a custom event to the root element of the modal
   *
   * @private
   *
   * @param {string} event Event name
   * @param {?Object} data Event data
   *
   * @return {void}
   */
  #dispatchEvent(event, data = null) {
    this.#element.dispatchEvent(new CustomEvent(event, { detail: data }));
  }

  /**
   * Initialize listeners and default state
   *
   * @private
   *
   * @return {void}
   */
  #initListeners() {
    // close after clicking on the close button or outside the modal
    this.#element.addEventListener('click', (e) => {
      if (
        e.target.classList.contains('lcas-modal-container') ||
        e.target.classList.contains('lcas-modal-close')
      ) {
        this.close();
      }
    });

    // switch form
    this.#switcher.on('switcher_change', (e) =>
      this.switchForm(e.detail.option)
    );
  }

  #initForms() {
    this.#forms = {};
    this.#element
      .querySelectorAll('.lcas-form-wrapper form')
      .forEach((form) => {
        let action = form.querySelector('input[name="action"]')?.value ?? '';
        if (!action) {
          return;
        }

        this.#forms[action] = new LCASForm(form, this.#config);
      });

    this.switchForm(this.#switcher.value());
  }

  /**
   * Add a listener to the root element
   *
   * @param {string} event Event name
   * @param {EventListener|Function} listener Event listener or callback function
   * @param {?Object} options Listener options or useCapture parameter
   *
   * @return {void}
   */
  on(event, listener, options = null) {
    this.#element.addEventListener(event, listener, options);
  }

  /**
   * Remove a listener from the root element
   *
   * @param {string} event Event name
   * @param {EventListener|Function} listener Event listener or callback function
   * @param {?Object} options Listener options or useCapture parameter
   *
   * @return {void}
   */
  off(event, listener, options = null) {
    this.#element.removeEventListener(event, listener, options);
  }

  /**
   * Open modal and send event
   *
   * @param {string} option Selected option
   * @param {boolean} lock Lock modal on selected option
   *
   * @return {void}
   */
  open(option = '', lock = false) {
    if (option) {
      this.#switcher.select(option, lock);
    }

    this.#element.classList.add('active');
    this.#dispatchEvent('modal_open');
  }

  /**
   * Close modal and send event
   *
   * @return {void}
   */
  close() {
    this.#element.classList.remove('active');
    this.#element
      .querySelectorAll('.lcas-form-alert')
      .forEach((alert) => alert.remove());
    this.#dispatchEvent('modal_close');
  }

  /**
   * Change displayed form
   *
   * @param {string} form Form key
   *
   * @return {void}
   */
  switchForm(form) {
    if (!(form in this.#forms) || this.#currentForm === form) {
      return;
    }

    if (this.#currentForm) {
      this.#forms[this.#currentForm].hide();
    }

    this.#currentForm = form;
    this.#forms[this.#currentForm].show();
  }

  /**
   * Updates the list of post types to which the user can subscribe
   *
   * @param {string[]} postTypes Post types
   *
   * @return {void}
   */
  setPostTypes(postTypes = []) {
    const form = this.#forms['lcas_form_subscribe'] ?? null;
    if (!form) {
      return;
    }

    const select = form.element.querySelector('#licornAlertSubType');
    if (!select) {
      return;
    }

    select.querySelectorAll('option').forEach((option) => {
      if (!option.value) {
        return;
      }

      if (postTypes.length <= 0 || postTypes.includes(option.value)) {
        option.disabled = false;
        option.classList.remove('hidden');
      } else {
        option.disabled = true;
        option.classList.add('hidden');
      }
    });

    form.reset();
  }
}
